import {
	ArrowLeft,
	ClipboardList,
	History,
	Lock,
	Package,
	Users,
} from "lucide-react";
import { useRouter } from "@tanstack/react-router";

import { CheckOperation } from "@workspace/operations/check-operation";
import type { Operations } from "@workspace/operations/types";
import type { StockItem } from "@workspace/product-catalog-types";
import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";
import {
	useVenueSettings,
	type StaffMember,
} from "@workspace/venue-management";

import { DigitalClock } from "../../components/digital-clock";

type EposHeaderProps = {
	staffMember: StaffMember | null | undefined;
	currentView: string;
	onStaffSwitch: () => void;
	onViewChange: (
		view:
			| "products"
			| "payment"
			| "settings"
			| "refund"
			| "refund-history"
			| "orders",
	) => void;
	stockStatus: StockItem["status"];
	timeZone: string;
	tenant: string;
	onOpenDrawer: () => void;
} & Operations;

export function EposHeader({ staffMember, ...props }: EposHeaderProps) {
	return (
		<div className="flex flex-col justify-between gap-4 pb-2 md:flex-row md:items-center">
			<EposHeaderUser staffMember={staffMember} />
			<EposHeaderButtons staffMember={staffMember} {...props} />
		</div>
	);
}

export function EposHeaderUser({
	staffMember,
	className,
}: Pick<EposHeaderProps, "staffMember"> & {
	className?: string;
}) {
	return (
		<div className={cn("flex items-center gap-4", className)}>
			<div>
				<h2 className="text-2xl font-bold">
					{staffMember?.personalDetails.preferredName}
				</h2>
			</div>
		</div>
	);
}

export function EposHeaderButtons({
	staffMember,
	onStaffSwitch,
	currentView,
	onViewChange,
	stockStatus,
	timeZone,
	tenant,
	onOpenDrawer,
	operations,
}: EposHeaderProps) {
	const router = useRouter();
	const { venueId } = useVenueSettings();

	const handleBackButton = () => {
		if (currentView === "products") {
			onStaffSwitch();
		} else if (currentView === "payment") {
			onViewChange("products");
		} else if (currentView === "staff") {
			router.navigate({
				to: "/$tenant/venues/$venueId",
				params: { tenant, venueId },
			});
		} else {
			onViewChange("products");
		}
	};

	return (
		<>
			<div className=" space-y-1">
				<DigitalClock
					timeZone={timeZone}
					className="hidden rounded-md bg-muted p-1 md:flex"
				/>
				<div className="hidden items-center gap-2 rounded-md bg-muted p-1 md:flex">
					<Package className="size-4 text-amber-500" />
					<span className="text-sm font-medium">
						Stock:{" "}
						{stockStatus === "Ok" && (
							<span className="text-green-500">Good</span>
						)}
						{stockStatus !== "Ok" && <span className="text-red-500">Low</span>}
					</span>
					<span
						className={cn("size-2 rounded-full ", {
							"bg-green-500": stockStatus === "Ok",
							"bg-red-500": stockStatus !== "Ok",
						})}
					></span>
				</div>
			</div>
			<div className="flex items-center gap-2">
				<Button
					variant="outline"
					size="lg"
					className="flex h-16 w-20 flex-col items-center justify-center gap-1 px-2 py-1 text-center"
					onClick={handleBackButton}
				>
					<ArrowLeft className="size-6" />
					<span className="text-sm">Back</span>
				</Button>
				<CheckOperation
					operation="staff-terminal-open-cash-drawer"
					operations={operations}
				>
					<Button
						variant="outline"
						size="lg"
						className="flex h-16 w-22 flex-col items-center justify-center gap-1 px-2 py-1 text-center"
						onClick={() => onOpenDrawer()}
					>
						<Lock className="size-6" />
						<span className="text-sm">Open Drawer</span>
					</Button>
				</CheckOperation>
				<CheckOperation
					operation="staff-terminal-view-historic-orders"
					operations={operations}
				>
					{staffMember && currentView !== "orders" && (
						<Button
							variant="outline"
							size="lg"
							className="flex h-16 w-20 flex-col items-center justify-center gap-1 px-2 py-1 text-center"
							onClick={() => onViewChange("orders")}
						>
							<ClipboardList className="size-6" />
							<span className="text-sm">Orders</span>
						</Button>
					)}
				</CheckOperation>
				<CheckOperation
					operation="staff-terminal-refund"
					operations={operations}
				>
					{currentView !== "refund-history" && (
						<Button
							variant="outline"
							size="lg"
							className="flex h-16 w-20 flex-col items-center justify-center gap-1 px-2 py-1 text-center"
							onClick={() => onViewChange("refund-history")}
						>
							<History className="size-6" />
							<span className="text-sm">Refunds</span>
						</Button>
					)}
				</CheckOperation>
				{currentView !== "staff" && (
					<Button
						variant="outline"
						size="lg"
						className="flex h-16 w-20 flex-col items-center justify-center gap-1 px-2 py-1 text-center"
						onClick={() => onStaffSwitch()}
					>
						<Users className="size-6" />
						<span className="text-sm">Staff</span>
					</Button>
				)}
			</div>
		</>
	);
}
